import { useState, useEffect, useCallback } from "react";
import { collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import { T } from "../theme";
import Avatar from "../components/Avatar";
import LangBadge from "../components/LangBadge";
import PullToRefresh from "../components/PullToRefresh";

export default function ConvoListScreen({
  conversations,
  currentUser,
  onSelect,
  onNewChat,
  onSignOut,
  requestNotifications,
  notificationsEnabled,
}) {
  const [notifDismissed, setNotifDismissed] = useState(false);
  const showNotifBanner = !notificationsEnabled && !notifDismissed && requestNotifications;
  // Cache of user profiles keyed by uid (for displaying participant names)
  const [userCache, setUserCache] = useState({});

  // Fetch display info for all unique participants we haven't cached yet
  useEffect(() => {
    const allUids = new Set();
    conversations.forEach((c) =>
      c.participants.forEach((uid) => {
        if (uid !== currentUser.uid && !userCache[uid]) allUids.add(uid);
      })
    );
    if (allUids.size === 0) return;

    // Firestore 'in' queries support max 30 items
    const chunks = [...allUids];
    const batchSize = 30;
    const promises = [];
    for (let i = 0; i < chunks.length; i += batchSize) {
      const batch = chunks.slice(i, i + batchSize);
      promises.push(
        getDocs(
          query(collection(db, "users"), where("__name__", "in", batch))
        )
      );
    }

    Promise.all(promises).then((snaps) => {
      const newCache = { ...userCache };
      snaps.forEach((snap) =>
        snap.docs.forEach((d) => {
          newCache[d.id] = { uid: d.id, ...d.data() };
        })
      );
      setUserCache(newCache);
    });
  }, [conversations, currentUser.uid]);

  // Helper to get display info for a uid
  const getUser = (uid) =>
    userCache[uid] || { uid, displayName: "...", preferredLanguage: "en" };

  // Pull-to-refresh: check for SW update + hard reload
  const handleRefresh = useCallback(async () => {
    if ("serviceWorker" in navigator) {
      const reg = await navigator.serviceWorker.getRegistration();
      if (reg) {
        await reg.update();
        if (reg.waiting) {
          reg.waiting.postMessage({ type: "SKIP_WAITING" });
        }
      }
    }
    window.location.reload();
  }, []);

  // Format timestamp for convo list
  const formatTime = (ts) => {
    if (!ts) return "";
    const date = ts.toDate ? ts.toDate() : new Date(ts);
    const now = new Date();
    const diff = now - date;
    if (diff < 86400000) {
      return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    }
    if (diff < 172800000) return "Yesterday";
    return date.toLocaleDateString([], { weekday: "short" });
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        background: T.bg0,
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: "calc(16px + env(safe-area-inset-top, 0px)) 20px 12px",
          borderBottom: `1px solid ${T.border}`,
          background: T.bg1,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div>
            <div
              style={{
                fontFamily: "'Barlow Condensed', sans-serif",
                fontSize: 26,
                fontWeight: 900,
                letterSpacing: -0.5,
              }}
            >
              CREW<span style={{ color: T.orange }}>LINK</span>
            </div>
            <div style={{ fontSize: 12, color: T.textDim, marginTop: 1 }}>
              {currentUser.displayName} ·{" "}
              <LangBadge lang={currentUser.preferredLanguage} />
              <button
                onClick={onSignOut}
                style={{
                  background: "none",
                  border: "none",
                  color: T.textMute,
                  fontSize: 11,
                  marginLeft: 8,
                  cursor: "pointer",
                  textDecoration: "underline",
                }}
              >
                Sign out
              </button>
            </div>
          </div>
          <button
            onClick={onNewChat}
            style={{
              background: T.orange,
              border: "none",
              borderRadius: 8,
              width: 44,
              height: 44,
              fontSize: 22,
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            +
          </button>
        </div>
      </div>

      {/* Notification permission banner */}
      {showNotifBanner && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "12px 16px",
            background: "rgba(255,107,26,0.12)",
            borderBottom: `1px solid ${T.border}`,
            flexShrink: 0,
          }}
        >
          <span style={{ fontSize: 20 }}>🔔</span>
          <div style={{ flex: 1, fontSize: 13, color: T.text, lineHeight: 1.4 }}>
            Enable notifications so you don't miss messages.
          </div>
          <button
            onClick={async () => {
              const ok = await requestNotifications();
              if (ok) setNotifDismissed(true);
            }}
            style={{
              padding: "8px 14px",
              background: T.orange,
              border: "none",
              borderRadius: 8,
              color: "#fff",
              fontSize: 13,
              fontWeight: 700,
              cursor: "pointer",
              flexShrink: 0,
            }}
          >
            Enable
          </button>
          <button
            onClick={() => setNotifDismissed(true)}
            style={{
              background: "none",
              border: "none",
              color: T.textMute,
              fontSize: 18,
              cursor: "pointer",
              padding: "0 4px",
            }}
          >
            ✕
          </button>
        </div>
      )}

      {/* Conversation list */}
      <PullToRefresh onRefresh={handleRefresh}>
        {conversations.length === 0 && (
          <div
            style={{
              textAlign: "center",
              color: T.textMute,
              padding: "60px 30px",
              fontSize: 14,
              lineHeight: 1.7,
            }}
          >
            <div style={{ fontSize: 36, marginBottom: 12 }}>💬</div>
            No conversations yet.
            <br />
            Tap <strong>+</strong> to start a new chat.
          </div>
        )}
        {conversations.map((c, i) => {
          const otherUids = c.participants.filter(
            (uid) => uid !== currentUser.uid
          );
          const others = otherUids.map(getUser);
          const isGroup = others.length > 1;
          const autoLabel = isGroup
            ? others
                .map((u) => (u.displayName || "").split(" ")[0])
                .join(", ")
            : others[0]?.displayName || "Chat";
          const label = isGroup && c.groupName ? c.groupName : autoLabel;

          return (
            <div
              key={c.id}
              onClick={() => onSelect(c.id)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 14,
                padding: "14px 20px",
                borderBottom: `1px solid ${T.border}`,
                background: T.bg0,
                cursor: "pointer",
                animation: `fadeUp 0.3s ease ${i * 0.07}s both`,
                transition: "background 0.15s",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.background = T.bg1)
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.background = T.bg0)
              }
            >
              <div
                style={{
                  position: "relative",
                  flexShrink: 0,
                  width: 50,
                  height: 50,
                }}
              >
                <Avatar user={others[0]} size={isGroup ? 38 : 50} />
                {isGroup && (
                  <div style={{ position: "absolute", bottom: 0, right: 0 }}>
                    <Avatar user={others[1]} size={30} />
                  </div>
                )}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "baseline",
                  }}
                >
                  <span style={{ fontWeight: 700, fontSize: 15, color: T.text }}>
                    {label}
                  </span>
                  <span
                    style={{
                      fontSize: 11,
                      color: T.textDim,
                      flexShrink: 0,
                      marginLeft: 8,
                    }}
                  >
                    {formatTime(c.lastMessageAt)}
                  </span>
                </div>
                <div
                  style={{
                    fontSize: 13,
                    color: T.textDim,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    marginTop: 3,
                  }}
                >
                  {c.lastMessage || "Start the conversation..."}
                </div>
              </div>
            </div>
          );
        })}
      </PullToRefresh>
    </div>
  );
}
